// pmoClient.js — klient mot den lokala PMO-RPA-agenten (Aion Notes-desktop).
//
// Bakgrund (Tasks/pmo_export_plan.md, AVGJORT 2026-09-06): journaltext förs över
// till PMO genom att POSTa till en LOKAL RPA-agent som redan körs på klinikdatorn
// (samma agent som Aion Notes använder). Agenten lyssnar på 127.0.0.1:5011 och
// klistrar varje rubrik sekventiellt uppifrån-och-ned i den PMO-post som råkar
// vara öppen i Citrix. Web-only funkar (Chrome/Firefox) tack vare PNA-headern i
// agenten — inget Electron-skal behövs.
//
// HÅRDA REGLER som denna klient upprätthåller:
//  - INGEN patientidentitet skickas: kontraktet är ENBART { rubriker: [{title, content}] }.
//    Aldrig pnr, token eller namn över gränsen (jfr ARKITEKTUR §8.2).
//  - `title` måste alltid vara en sträng — en loggrad i agenten kraschar annars.
//  - Journal kan INTE starta agenten (ingen window.aionDesktop utan Electron).
//    Svarar 5011 inte → maskinen saknar agenten → degradera snyggt.

// Lokala agenten. Loopback är "potentially trustworthy" så https-origin får nå den;
// agenten svarar med Access-Control-Allow-Private-Network: true på PNA-preflighten.
export const PMO_AGENT_URL = 'http://127.0.0.1:5011/send';

// Hur länge vi väntar på agenten innan vi tolkar det som "körs inte".
const PMO_TIMEOUT_MS = 8000;

/**
 * Serialisera en anteknings sektioner till agentens kontrakt.
 *
 * v1 SPEGLAR Notes: sektionerna skickas som array i BEFINTLIG ordning, 1-till-1.
 * Agentens RPA navigerar mellan PMO-fälten med ren nedåtpil, så ordningen (och att
 * varje sektion skickas) är det som håller fältmappningen rätt. Både `title` och
 * `content` skickas; agenten klistrar bara `content` men `title` krävs för loggraden.
 *
 * @param {object} content - Anteckningens content, { sections: [{ title, content }] }
 * @returns {Array<{title: string, content: string}>} rubriker i ordning
 */
export const serializeSectionsForPmo = (content) => {
  const sections = (content && Array.isArray(content.sections)) ? content.sections : [];
  return sections.map((s) => ({
    // title får aldrig saknas (agentens loggrad kraschar på undefined).
    title: (s && typeof s.title === 'string' && s.title) ? s.title : ' ',
    // content skickas som-är; tomt fält = tom sträng (fältet hoppas ändå över
    // med nedåtpil, så ordningen bevaras).
    content: (s && typeof s.content === 'string') ? s.content : '',
  }));
};

/**
 * Resultatstatus från ett överföringsförsök.
 * @typedef {Object} PmoTransferResult
 * @property {'ok'|'agent_error'|'agent_down'} outcome
 * @property {number} [status]  HTTP-status när agenten svarade
 * @property {string} [detail]  Ev. felmeddelande/text
 */

/**
 * För över rubriker till den lokala PMO-agenten.
 *
 * @param {Array<{title: string, content: string}>} rubriker
 * @returns {Promise<PmoTransferResult>}
 *   - outcome 'ok'         : agenten svarade 2xx (inklistring körd i öppen PMO-post)
 *   - outcome 'agent_error': agenten svarade men med fel (t.ex. 500 = PMO ej öppet/fokuserat)
 *   - outcome 'agent_down' : ingen kontakt med 5011 (agenten körs inte på maskinen)
 */
export const transferToPmo = async (rubriker) => {
  const payload = { rubriker };
  // Logga payloaden så den kan verifieras i konsolen/nätverksfliken.
  // OBS: INGEN patientidentitet ingår — bara title + content.
  // eslint-disable-next-line no-console
  console.log('[PMO] Överför till agenten', PMO_AGENT_URL, payload);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PMO_TIMEOUT_MS);

  try {
    const res = await fetch(PMO_AGENT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (res.ok) {
      return { outcome: 'ok', status: res.status };
    }
    // Agenten svarade men kraschade (vanligast: 500 för att PMO/Citrix inte var
    // öppet eller fältet inte var i fokus). Transporten funkade — RPA:n gjorde inte.
    let detail = '';
    try { detail = await res.text(); } catch (_) { /* strunt */ }
    return { outcome: 'agent_error', status: res.status, detail };
  } catch (err) {
    // TypeError "Failed to fetch"/abort = ingen kontakt med agenten på 5011.
    return { outcome: 'agent_down', detail: err && err.message };
  } finally {
    clearTimeout(timer);
  }
};
